import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useQueryClient } from "@tanstack/react-query"

interface CreateWorkspaceDialogProps {
  onWorkspaceCreated?: () => void;
}

export function CreateWorkspaceDialog({ onWorkspaceCreated }: CreateWorkspaceDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [workspaceName, setWorkspaceName] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim()) {
      setError(t("common.workspaces.nameRequired"))
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      
      if (userError || !user) {
        console.error("User error:", userError)
        setError(t("common.auth.userNotFound"))
        return
      }

      const { data: workspace, error: workspaceError } = await supabase
        .from("workspaces")
        .insert([{ 
          name: workspaceName.trim()
        }])
        .select()
        .single()

      if (workspaceError) {
        console.error("Workspace creation error:", workspaceError)
        throw workspaceError
      }

      if (!workspace) {
        throw new Error("No workspace returned after creation")
      }

      toast({
        title: t("common.workspaces.created"),
        description: t("common.workspaces.createdDescription"),
      })
      
      // Invalidate the workspaces query to trigger a refresh
      queryClient.invalidateQueries({ queryKey: ['workspaces-with-boards'] })
      
      setOpen(false)
      setWorkspaceName("")
      onWorkspaceCreated?.()
    } catch (error: any) {
      console.error("Error in workspace creation:", error)
      if (error.code === '42501') {
        setError(t("common.workspaces.notAuthorized"))
      } else {
        setError(t("common.workspaces.errorCreating"))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          {t("common.workspaces.create")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.workspaces.createNew")}</DialogTitle>
          <DialogDescription>
            {t("common.workspaces.createDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="name">{t("common.workspaces.name")}</Label>
            <Input
              id="name"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder={t("common.workspaces.namePlaceholder")}
              disabled={isLoading}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="submit"
            onClick={handleCreateWorkspace}
            disabled={isLoading || !workspaceName.trim()}
          >
            {isLoading ? t("common.loading") : t("common.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}